import React, { useEffect, useRef, useState } from 'react'
import { apiJson } from '../../lib/api'
import { addDaysLocal, todayLocal } from '../timesheets/time'
import { kg } from './structure'
import pa from '../ProductActivityPage.module.css'
import mv from '../SitesMapPage.module.css'

// GPS yield on the Map view (handoff §7g, "GPS resolution, stated on the
// page"): where the Site's picks were by their recorded GPS, against the
// boundaries drawn now. The rules are the server's
// (services/gps-attribution.js) and its statement is shown as sent. This is
// analysis only; nothing here changes a pick, its job, its variety or pay.

export interface GpsBucket {
  picks: number
  kg: number
  by_product: { product: string | null; picks: number; kg: number }[]
}

export interface GpsLocation extends GpsBucket {
  node_id: string
  level: number
  name: string
  path: string
  mapped: boolean | null
  recorded_elsewhere: number
}

export interface GpsYieldResult {
  site: { id: string; name: string; map_level: number }
  from: string
  to: string
  basis: 'current_boundaries'
  basis_label: string
  statement: string
  map_level: number
  boundaries: number
  totals: { picks: number; kg: number; with_gps: number }
  locations: GpsLocation[]
  outside: GpsBucket & { label: string; recorded_to_a_location: number }
  ambiguous: GpsBucket & { label: string; candidates: { node_id: string; path: string; picks: number }[] }
  no_gps: GpsBucket & { label: string; by_job: { job_id: string | null; job_name: string | null; picks: number; kg: number }[] }
}

const picks = (n: number) => `${n.toLocaleString('en-NZ')} pick${n === 1 ? '' : 's'}`
const varieties = (b: GpsBucket) => b.by_product.map((p) => `${p.product || 'No variety'} ${kg(p.kg)}`).join(' · ')

// The last seven days to today, as a starting range.
export function defaultRange(today = todayLocal()): { from: string; to: string } {
  return { from: addDaysLocal(today, -6), to: today }
}

export function GpsYield({
  user,
  siteId,
  levelLabel,
  // Read again when the boundaries change.
  version,
  onSelect,
}: {
  user: any
  siteId: string
  levelLabel: string
  version: string
  onSelect: (nodeId: string) => void
}) {
  const [range, setRange] = useState(defaultRange)
  const [data, setData] = useState<GpsYieldResult | null>(null)
  const [error, setError] = useState('')
  const seq = useRef(0)

  useEffect(() => {
    if (!siteId || !range.from || !range.to) return
    const n = ++seq.current
    setError('')
    apiJson<GpsYieldResult>(user, `/api/sites/${siteId}/gps-yield?from=${range.from}&to=${range.to}`)
      .then((r) => n === seq.current && setData(r))
      .catch((e: any) => {
        if (n !== seq.current) return
        setData(null)
        setError(e?.message || 'Could not load the GPS yield')
      })
  }, [user?.uid, siteId, range.from, range.to, version])

  const level = data?.map_level
  const bucketRow = (key: string, label: string, b: GpsBucket, detail: React.ReactNode) => (
    <tr key={key} className={mv.gpsOther}>
      <td>
        <div className={mv.gpsName}>{label}</div>
        {detail && <div className={mv.gpsDetail}>{detail}</div>}
      </td>
      <td className={mv.gpsNum}>{b.picks.toLocaleString('en-NZ')}</td>
      <td className={mv.gpsNum}>{kg(b.kg)}</td>
      <td className={mv.gpsDetail}>{varieties(b)}</td>
    </tr>
  )

  return (
    <section className={`${pa.card} ${mv.gps}`} aria-label="GPS yield">
      <div className={mv.gpsHead}>
        <h2 className={mv.gpsTitle}>GPS yield</h2>
        <span className={mv.gpsBasis}>{data?.basis_label || 'Using current boundaries'}</span>
        <label className={mv.gpsDate}>
          From
          <input type="date" value={range.from} max={range.to} onChange={(e) => e.target.value && setRange((r) => ({ ...r, from: e.target.value }))} />
        </label>
        <label className={mv.gpsDate}>
          To
          <input type="date" value={range.to} min={range.from} onChange={(e) => e.target.value && setRange((r) => ({ ...r, to: e.target.value }))} />
        </label>
      </div>
      {data && <p className={mv.gpsStatement}>{data.statement}</p>}
      {error && (
        <p className={mv.note} role="alert">
          {error}
        </p>
      )}
      {data && (
        <>
          <p className={mv.hint} data-testid="gps-totals">
            {picks(data.totals.picks)}, {kg(data.totals.kg)} · {data.totals.with_gps.toLocaleString('en-NZ')} with GPS · {data.boundaries}{' '}
            {data.boundaries === 1 ? 'boundary' : 'boundaries'} drawn
          </p>
          <table className={mv.gpsTable}>
            <thead>
              <tr>
                <th>Location</th>
                <th className={mv.gpsNum}>Picks</th>
                <th className={mv.gpsNum}>Credited</th>
                <th>By variety</th>
              </tr>
            </thead>
            <tbody>
              {data.locations.map((l) => (
                <tr key={l.node_id} className={l.level === level ? undefined : mv.gpsUp}>
                  <td>
                    {l.level === level ? (
                      <button type="button" className={mv.link} onClick={() => onSelect(l.node_id)}>
                        {l.path}
                      </button>
                    ) : (
                      <span className={mv.gpsName}>{l.path}</span>
                    )}
                    {l.level === level && !l.mapped && <div className={mv.gpsDetail}>Boundary removed or not drawn</div>}
                    {l.recorded_elsewhere > 0 && (
                      <div className={mv.gpsDetail}>
                        {picks(l.recorded_elsewhere)} recorded to another {levelLabel.toLowerCase()}
                      </div>
                    )}
                  </td>
                  <td className={mv.gpsNum}>{l.picks.toLocaleString('en-NZ')}</td>
                  <td className={mv.gpsNum}>{kg(l.kg)}</td>
                  <td className={mv.gpsDetail}>{varieties(l)}</td>
                </tr>
              ))}
              {bucketRow(
                'outside',
                data.outside.label,
                data.outside,
                data.outside.recorded_to_a_location > 0 ? `${picks(data.outside.recorded_to_a_location)} recorded to a location` : null,
              )}
              {bucketRow(
                'ambiguous',
                data.ambiguous.label,
                data.ambiguous,
                data.ambiguous.candidates.length ? `Touching ${data.ambiguous.candidates.map((c) => c.path).join(', ')}; counted in no location` : null,
              )}
              {bucketRow(
                'no-gps',
                data.no_gps.label,
                data.no_gps,
                data.no_gps.by_job.length
                  ? `Kept under the recorded job: ${data.no_gps.by_job.map((j) => `${j.job_name || 'No job'} ${kg(j.kg)}`).join(', ')}`
                  : null,
              )}
            </tbody>
          </table>
          {!data.boundaries && <p className={mv.hint}>No boundaries are drawn, so every pick with GPS reads Outside mapped areas.</p>}
        </>
      )}
    </section>
  )
}
